import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft, ArrowRight, Check, RefreshCw, Shuffle, X } from 'lucide-react';
import { db } from '../../utils/firebase';
import type { ChangeOrder, ProposalBuilderData } from '../../types';
import type {
  SuretyContractSummaryEnclosure,
  SuretyEnclosureDocs,
  SuretySchedulePhase,
  SuretyScheduleEnclosure,
} from '../../types/regulatory';
import { usePaymentLagSettings } from '../../hooks/usePaymentLagSettings';
import { subscribeChangeOrdersForDeal } from '../../utils/changeOrders';
import { getSignedCommercialListTotal } from '../../utils/proposalPublicGrandTotals';
import type { TaskGroupLike } from '../../utils/paymentDueFlags';
import {
  SURETY_SCHEDULE_PHASES,
  buildContractSummary,
  enclosureLabels,
  phaseColor,
  phaseScheduleFromTasks,
  scheduleChartLayout,
  scheduleCompletionDate,
  scheduleStartDate,
  simulatePhaseSchedule,
  totalScheduleDays,
  type SchedulePhaseTaskLike,
} from '../../utils/suretyEnclosures';
import { formatBondMoney, longDateFromYmd, ymdLocal, type SuretyBondRequestDraft } from '../../utils/suretyBondRequest';
import FieldHelp from './FieldHelp';

export interface SuretyEnclosureWizardResult {
  docs: SuretyEnclosureDocs;
  /** Set when the user asked to push the schedule's dates back onto the request. */
  startDate?: string | null;
  completionDate?: string | null;
}

export interface SuretyEnclosureWizardModalProps {
  request: SuretyBondRequestDraft;
  scheduleTasks: SchedulePhaseTaskLike[];
  taskGroups: TaskGroupLike[];
  onClose: () => void;
  onApply: (result: SuretyEnclosureWizardResult) => void;
  /** Pre-pick enclosures (overrides what the request already has). */
  preset?: { schedule: boolean; contractSummary: boolean };
  /** Skip the pick step and open here — used when the wizard runs right before a download. */
  startAt?: 'schedule' | 'contract';
  /** Caption for the primary button on the review step; default "Apply". */
  applyLabel?: string;
  /** Shown above the first step so the user knows why the wizard interrupted them. */
  intro?: string;
}

type StepKey = 'pick' | 'schedule' | 'contract' | 'review';

const SOURCE_LABEL: Record<SuretySchedulePhase['source'], string> = {
  tasks: 'Hub tasks',
  simulated: 'Simulated',
  manual: 'Edited',
};

function defaultSimWindow(request: SuretyBondRequestDraft): { start: string; end: string } {
  const today = ymdLocal(new Date());
  const start = request.startDate || request.contractDate || request.bidDate || today;
  let end = request.completionDate || '';
  if (!end) {
    const d = new Date(start);
    d.setMonth(d.getMonth() + 9);
    end = ymdLocal(d);
  }
  return { start, end };
}

const SuretyEnclosureWizardModal: React.FC<SuretyEnclosureWizardModalProps> = ({
  request,
  scheduleTasks,
  taskGroups,
  onClose,
  onApply,
  preset,
  startAt,
  applyLabel,
  intro,
}) => {
  const { lagSettings } = usePaymentLagSettings();
  const existing = request.enclosureDocs ?? null;

  const [includeSchedule, setIncludeSchedule] = useState<boolean>(
    preset ? preset.schedule : existing ? !!existing.schedule : true,
  );
  const [includeContract, setIncludeContract] = useState<boolean>(
    preset ? preset.contractSummary : existing ? !!existing.contractSummary : true,
  );
  const [step, setStep] = useState<StepKey>(startAt ?? 'pick');

  // --- Schedule ---------------------------------------------------------------
  const hubPhases = useMemo(
    () => (request.projectId ? phaseScheduleFromTasks(request.projectId, scheduleTasks, taskGroups) : []),
    [request.projectId, scheduleTasks, taskGroups],
  );
  const [simWindow, setSimWindow] = useState(() => defaultSimWindow(request));
  const [phases, setPhases] = useState<SuretySchedulePhase[]>(() => {
    if (existing?.schedule?.phases?.length) return existing.schedule.phases;
    if (hubPhases.length) return hubPhases;
    const w = defaultSimWindow(request);
    return simulatePhaseSchedule(w.start, w.end);
  });
  const [scheduleNote, setScheduleNote] = useState<string>(existing?.schedule?.note ?? '');
  const [syncDates, setSyncDates] = useState(true);

  const reloadFromHub = () => setPhases(hubPhases);
  const resimulate = () => setPhases(simulatePhaseSchedule(simWindow.start, simWindow.end));
  const editPhase = (i: number, field: 'startDate' | 'endDate', value: string) => {
    setPhases((prev) => prev.map((p, j) => (j === i ? { ...p, [field]: value, source: 'manual' } : p)));
  };
  const removePhase = (i: number) => setPhases((prev) => prev.filter((_, j) => j !== i));
  const addMissingPhase = (key: string) => {
    const def = SURETY_SCHEDULE_PHASES.find((p) => p.key === key);
    if (!def) return;
    const last = phases[phases.length - 1];
    const start = last ? last.endDate : simWindow.start;
    setPhases((prev) => [...prev, { key: def.key, name: def.name, startDate: start, endDate: start, source: 'manual' }]);
  };

  const chart = useMemo(() => scheduleChartLayout(phases), [phases]);
  const schedCompletion = scheduleCompletionDate(phases);
  const schedStart = scheduleStartDate(phases);
  const datesDiffer = (schedCompletion && schedCompletion !== request.completionDate)
    || (schedStart && schedStart !== request.startDate);
  const scheduleProblems = phases
    .filter((p) => !p.startDate || !p.endDate || p.endDate < p.startDate)
    .map((p) => `${p.name}: end must be on or after start`);

  // --- Contract summary -------------------------------------------------------
  const [signedBase, setSignedBase] = useState<number | null | undefined>(undefined);
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[] | undefined>(undefined);
  const [contractError, setContractError] = useState<string | null>(null);

  const loadContract = useCallback(() => {
    if (!request.dealId) {
      setSignedBase(null);
      setChangeOrders([]);
      return () => undefined;
    }
    let cancelled = false;
    setContractError(null);
    getDoc(doc(db, 'proposalBuilders', request.dealId))
      .then((snap) => {
        if (cancelled) return;
        setSignedBase(snap.exists() ? getSignedCommercialListTotal(snap.data() as ProposalBuilderData) : null);
      })
      .catch((err) => {
        if (cancelled) return;
        setSignedBase(null);
        setContractError(err instanceof Error ? err.message : 'Could not read the signed proposal');
      });
    // One-shot: take the first snapshot and detach.
    const unsub = subscribeChangeOrdersForDeal(
      request.dealId,
      (orders) => {
        if (!cancelled) setChangeOrders(orders);
        unsub();
      },
      (err) => {
        if (cancelled) return;
        setChangeOrders([]);
        setContractError(err instanceof Error ? err.message : 'Could not read change orders');
      },
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [request.dealId]);

  useEffect(() => {
    if (!includeContract) return undefined;
    return loadContract();
  }, [includeContract, loadContract]);

  const contractLoading = includeContract && (signedBase === undefined || changeOrders === undefined);
  const contractSummary: SuretyContractSummaryEnclosure | null = useMemo(() => {
    if (!includeContract || contractLoading) return null;
    return buildContractSummary({
      contractAmount: request.contractAmount,
      signedBase: signedBase ?? null,
      changeOrders: changeOrders ?? [],
      phases: includeSchedule ? phases : [],
      contractDate: request.contractDate,
      lag: lagSettings,
    });
  }, [includeContract, contractLoading, request.contractAmount, request.contractDate, signedBase, changeOrders, includeSchedule, phases, lagSettings]);

  // --- Steps -------------------------------------------------------------------
  const steps: StepKey[] = useMemo(() => {
    const s: StepKey[] = ['pick'];
    if (includeSchedule) s.push('schedule');
    if (includeContract) s.push('contract');
    s.push('review');
    return s;
  }, [includeSchedule, includeContract]);
  const stepIndex = Math.max(0, steps.indexOf(step));
  const canNext = step === 'pick'
    ? includeSchedule || includeContract
    : step === 'schedule'
      ? phases.length > 0 && scheduleProblems.length === 0
      : step === 'contract'
        ? !contractLoading
        : true;

  useEffect(() => {
    if (!steps.includes(step)) setStep(steps[Math.min(stepIndex, steps.length - 1)]);
  }, [steps, step, stepIndex]);

  const docs: SuretyEnclosureDocs = useMemo(() => {
    const schedule: SuretyScheduleEnclosure | null = includeSchedule && phases.length
      ? { phases, note: scheduleNote.trim(), builtAt: ymdLocal(new Date()) }
      : null;
    return { schedule, contractSummary: includeContract ? contractSummary : null };
  }, [includeSchedule, phases, scheduleNote, includeContract, contractSummary]);

  const resultingLabels = enclosureLabels(docs, request.enclosures);

  const apply = () => {
    const result: SuretyEnclosureWizardResult = { docs };
    if (includeSchedule && syncDates && datesDiffer) {
      result.startDate = schedStart;
      result.completionDate = schedCompletion;
    }
    onApply(result);
  };

  const title = step === 'pick'
    ? 'Which enclosures?'
    : step === 'schedule'
      ? 'Enclosure A — Project schedule'
      : step === 'contract'
        ? 'Enclosure B — Contract summary'
        : 'Review and apply';

  return (
    <div className="fin-modal-overlay" onClick={onClose}>
      <div
        className="fin-modal reg-modal reg-wizard"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Build enclosures"
      >
        <div className="fin-modal-header">
          <div>
            <h3>Build enclosures</h3>
            <div className="reg-wizard-steps" aria-label="Steps">
              {steps.map((s, i) => (
                <span key={s} className={`reg-wizard-step${i === stepIndex ? ' is-current' : i < stepIndex ? ' is-done' : ''}`}>
                  {i < stepIndex ? <Check size={11} /> : <span>{i + 1}</span>}
                  {s === 'pick' ? 'Enclosures' : s === 'schedule' ? 'Schedule' : s === 'contract' ? 'Contract' : 'Review'}
                </span>
              ))}
            </div>
          </div>
          <button className="fin-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="fin-modal-body">
          {intro && stepIndex === 0 && <p className="reg-wizard-intro">{intro}</p>}
          <h4 className="reg-wizard-title">{title}</h4>

          {step === 'pick' && (
            <div className="reg-wizard-pick">
              <label className="reg-wizard-option">
                <input type="checkbox" checked={includeSchedule} onChange={(e) => setIncludeSchedule(e.target.checked)} />
                <span>
                  <strong>Project schedule</strong>
                  <small>
                    One page with a phase table and bar chart — Design, Procurement, Production, Installation. Built from Hub
                    tasks when they exist, otherwise simulated across the start → completion window like the staff planner.
                  </small>
                </span>
              </label>
              <label className="reg-wizard-option">
                <input type="checkbox" checked={includeContract} onChange={(e) => setIncludeContract(e.target.checked)} />
                <span>
                  <strong>Contract summary</strong>
                  <small>
                    Signed base, approved change orders, and the 50 / 40 / 10 payment installments with due dates anchored to the
                    schedule. Sureties ask for this instead of the full signed proposal.
                  </small>
                </span>
              </label>
              {!includeSchedule && !includeContract && (
                <p className="fin-modal-help">Pick at least one enclosure to continue.</p>
              )}
            </div>
          )}

          {step === 'schedule' && (
            <div className="reg-wizard-schedule">
              <div className="reg-wizard-toolbar">
                <button type="button" className="fin-secondary-btn" onClick={reloadFromHub} disabled={hubPhases.length === 0}
                  title={hubPhases.length === 0 ? 'This project has no dated tasks in Design / Procurement / Production / Installation groups yet.' : 'Replace the table with phase spans from the Hub task groups.'}>
                  <RefreshCw size={13} /> Reload from Hub tasks
                </button>
                <div className="reg-wizard-sim">
                  <label>
                    <span>Start</span>
                    <input className="fin-modal-input" type="date" value={simWindow.start} onChange={(e) => setSimWindow((w) => ({ ...w, start: e.target.value }))} />
                  </label>
                  <label>
                    <span>Completion</span>
                    <input className="fin-modal-input" type="date" value={simWindow.end} onChange={(e) => setSimWindow((w) => ({ ...w, end: e.target.value }))} />
                  </label>
                  <button type="button" className="fin-secondary-btn" onClick={resimulate} disabled={!simWindow.start}
                    title="Split the window into Design 30% · Procurement 20% · Production 30% · Installation 20%.">
                    <Shuffle size={13} /> Re-simulate
                  </button>
                </div>
              </div>

              <table className="reg-table reg-wizard-table">
                <thead>
                  <tr>
                    <th>Phase</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Days</th>
                    <th>Source</th>
                    <th aria-label="Remove" />
                  </tr>
                </thead>
                <tbody>
                  {phases.map((p, i) => {
                    const days = chart?.bars.find((b) => b.key === p.key)?.days ?? 0;
                    return (
                      <tr key={`${p.key}-${i}`}>
                        <td>
                          <span className="reg-phase-dot" style={{ background: phaseColor(p.key) }} />
                          {p.name}
                        </td>
                        <td><input className="fin-modal-input" type="date" value={p.startDate} onChange={(e) => editPhase(i, 'startDate', e.target.value)} /></td>
                        <td><input className="fin-modal-input" type="date" value={p.endDate} onChange={(e) => editPhase(i, 'endDate', e.target.value)} /></td>
                        <td className="reg-num">{days || '—'}</td>
                        <td><span className={`reg-source-badge is-${p.source}`}>{SOURCE_LABEL[p.source]}</span></td>
                        <td>
                          <button type="button" className="fin-icon-btn danger" aria-label={`Remove ${p.name}`} onClick={() => removePhase(i)}>
                            <X size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {phases.length === 0 && (
                    <tr><td colSpan={6} className="reg-empty">No phases — reload from the Hub or simulate a window.</td></tr>
                  )}
                </tbody>
              </table>
              {SURETY_SCHEDULE_PHASES.some((d) => !phases.some((p) => p.key === d.key)) && (
                <div className="reg-wizard-add-phase">
                  <span>Add phase:</span>
                  {SURETY_SCHEDULE_PHASES.filter((d) => !phases.some((p) => p.key === d.key)).map((d) => (
                    <button key={d.key} type="button" className="fin-secondary-btn" onClick={() => addMissingPhase(d.key)}>{d.name}</button>
                  ))}
                </div>
              )}
              {scheduleProblems.length > 0 && (
                <ul className="reg-modal-problems">{scheduleProblems.map((p) => <li key={p}>{p}</li>)}</ul>
              )}

              {chart && (
                <div className="reg-chart" aria-label="Schedule preview">
                  <div className="reg-chart-ticks">
                    {chart.ticks.map((t) => (
                      <span key={t.label + t.x} style={{ left: `${t.x * 100}%` }}>{t.label}</span>
                    ))}
                  </div>
                  <div className="reg-chart-rows">
                    {chart.bars.map((b) => (
                      <div key={b.key} className="reg-chart-row">
                        <span className="reg-chart-label">{b.name}</span>
                        <div className="reg-chart-track">
                          {chart.ticks.map((t) => <i key={t.x} style={{ left: `${t.x * 100}%` }} />)}
                          <div className="reg-chart-bar" style={{ left: `${b.left * 100}%`, width: `${b.width * 100}%`, background: b.color }} />
                          {chart.today != null && <div className="reg-chart-today" style={{ left: `${chart.today * 100}%` }} />}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="reg-chart-summary">
                    {longDateFromYmd(schedStart)} → {longDateFromYmd(schedCompletion)} · {totalScheduleDays(phases)} days
                  </div>
                </div>
              )}

              <div className="fin-modal-field" style={{ marginTop: 10 }}>
                <label className="fin-modal-label" htmlFor="sew-note">
                  Note under the chart <FieldHelp text="Printed in small type under the schedule — say whether dates are from the Hub or simulated, and anything the surety should know about sequencing." />
                </label>
                <textarea id="sew-note" className="fin-modal-textarea" rows={2} value={scheduleNote} onChange={(e) => setScheduleNote(e.target.value)}
                  placeholder={phases.some((p) => p.source === 'simulated') ? 'Dates are a planning simulation; the Hub schedule will supersede once tasks are set.' : 'Optional'} />
              </div>

              {datesDiffer && (
                <label className="fin-modal-toggle" style={{ marginTop: 8 }}>
                  <input type="checkbox" checked={syncDates} onChange={(e) => setSyncDates(e.target.checked)} />
                  Update the request's Start / Substantial completion to {longDateFromYmd(schedStart)} → {longDateFromYmd(schedCompletion)}
                </label>
              )}
            </div>
          )}

          {step === 'contract' && (
            <div className="reg-wizard-contract">
              {contractError && <div className="fin-modal-error">{contractError}</div>}
              {contractLoading && <div className="reg-loading"><span className="reg-spin" /> Reading the signed proposal and change orders…</div>}
              {contractSummary && (
                <>
                  <div className="reg-amount-preview">
                    <span>{contractSummary.signedBase != null ? 'Signed proposal base' : 'Contract amount on this request (no signed proposal yet)'}</span>
                    <strong>{formatBondMoney(contractSummary.signedBase ?? request.contractAmount)}</strong>
                  </div>
                  <h5 className="reg-wizard-subtitle">Approved change orders</h5>
                  {contractSummary.changeOrders.length === 0 ? (
                    <p className="fin-modal-help">None approved.</p>
                  ) : (
                    <table className="reg-table reg-wizard-table">
                      <thead><tr><th>#</th><th>Title</th><th>Approved</th><th className="reg-num">Amount</th></tr></thead>
                      <tbody>
                        {contractSummary.changeOrders.map((co) => (
                          <tr key={co.number}>
                            <td>CO-{String(co.number).padStart(3, '0')}</td>
                            <td>{co.title}</td>
                            <td>{longDateFromYmd(co.approvedAt)}</td>
                            <td className="reg-num">{formatBondMoney(co.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <h5 className="reg-wizard-subtitle">Payment schedule</h5>
                  <table className="reg-table reg-wizard-table">
                    <thead><tr><th>Installment</th><th>Due</th><th>Basis</th><th className="reg-num">Amount</th></tr></thead>
                    <tbody>
                      {contractSummary.installments.map((row) => (
                        <tr key={row.id}>
                          <td>{row.label}</td>
                          <td>{row.dueDate ? longDateFromYmd(row.dueDate) : '—'}{row.estimated && row.dueDate ? <span className="reg-source-badge is-simulated" style={{ marginLeft: 6 }}>est.</span> : null}</td>
                          <td className="reg-muted">{row.basis}</td>
                          <td className="reg-num">{formatBondMoney(row.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3}><strong>Current contract value</strong></td>
                        <td className="reg-num"><strong>{formatBondMoney(contractSummary.currentContractValue)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                  {!includeSchedule && (
                    <p className="fin-modal-help">Pre-shipment and final due dates come from the schedule enclosure — include it to date them.</p>
                  )}
                  {contractSummary.currentContractValue !== request.contractAmount && (
                    <p className="fin-modal-help">
                      Heads up: the letter's contract amount is {formatBondMoney(request.contractAmount)}; this summary totals {formatBondMoney(contractSummary.currentContractValue)}.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {step === 'review' && (
            <div className="reg-wizard-review">
              <p>The letter's enclosures line will read:</p>
              <ul className="reg-wizard-labels">
                {resultingLabels.map((l) => <li key={l}>{l}</li>)}
              </ul>
              <ul className="reg-wizard-summary">
                {docs.schedule && (
                  <li>
                    <strong>Enclosure A</strong> — {docs.schedule.phases.length} phases, {longDateFromYmd(schedStart)} → {longDateFromYmd(schedCompletion)}
                    {docs.schedule.phases.some((p) => p.source === 'simulated') ? ' (simulated)' : ''}
                    {includeSchedule && syncDates && datesDiffer ? '; request dates will be updated' : ''}
                  </li>
                )}
                {docs.contractSummary && (
                  <li>
                    <strong>Enclosure B</strong> — {docs.contractSummary.changeOrders.length} approved change order{docs.contractSummary.changeOrders.length === 1 ? '' : 's'},
                    contract value {formatBondMoney(docs.contractSummary.currentContractValue)}
                  </li>
                )}
              </ul>
              <p className="fin-modal-help">Enclosures print as extra pages behind the letter. You can rebuild them any time.</p>
            </div>
          )}
        </div>

        <div className="fin-modal-footer">
          <button type="button" className="fin-secondary-btn" onClick={onClose}>Cancel</button>
          <div className="fin-modal-footer-right">
            {stepIndex > 0 && (
              <button type="button" className="fin-secondary-btn" onClick={() => setStep(steps[stepIndex - 1])}>
                <ArrowLeft size={13} /> Back
              </button>
            )}
            {step !== 'review' ? (
              <button type="button" className="fin-primary-btn" onClick={() => setStep(steps[stepIndex + 1])} disabled={!canNext}>
                Next <ArrowRight size={13} />
              </button>
            ) : (
              <button type="button" className="fin-primary-btn" onClick={apply} disabled={!docs.schedule && !docs.contractSummary}>
                <Check size={13} /> {applyLabel ?? 'Apply enclosures'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuretyEnclosureWizardModal;
